import { Link } from 'wouter';
import {
    CheckCircle2,
    CalendarDays,
    Link as LinkIcon,
    LayoutDashboard,
    Clock,
    BarChart3,
    ArrowRight,
} from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { usePageSEO } from '../utils/seo';

const SolutionsPage = () => {
    usePageSEO({
        title: 'Warehouse Dock Scheduling Solutions — BayWhiz',
        description:
            'See how BayWhiz helps warehouse, 3PL, cold storage, and distribution teams replace manual spreadsheet dock scheduling, streamline carrier appointment requests, and track dwell time.',
        canonicalPath: '/solutions',
    });

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-teal-500/20 font-sans overflow-x-hidden">
            <MarketingNav variant="light" />

            {/* 1. HERO */}
            <section className="pt-16 sm:pt-20 pb-16 px-4 sm:px-6 text-center bg-slate-50">
                <div className="max-w-4xl mx-auto">
                    <div className="text-teal-700 font-bold mb-4 tracking-widest uppercase text-xs">Solutions</div>
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold mb-6 text-slate-900 tracking-tight leading-tight">
                        Built for warehouses that have outgrown manual dock scheduling.
                    </h1>
                    <p className="text-base sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-10">
                        Whether the problem is carrier coordination, crowded appointment windows, dock reassignments, or limited dwell visibility, BayWhiz gives your team one shared schedule to work from.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-4 mb-6">
                        <Link
                            to="/request-trial"
                            className="w-full sm:w-auto px-8 py-4 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors shadow-md inline-flex items-center justify-center gap-2"
                        >
                            Request Free Trial
                            <ArrowRight size={18} />
                        </Link>
                        <Link
                            to="/product"
                            className="w-full sm:w-auto px-8 py-4 bg-white text-slate-800 border border-slate-300 rounded-xl font-semibold hover:bg-slate-50 transition-colors inline-flex items-center justify-center"
                        >
                            Explore the Product
                        </Link>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 font-medium">
                        30-day full-access trial &middot; No credit card required &middot; No WMS replacement required
                    </p>
                </div>
            </section>

            {/* 2. WHO BAYWHIZ IS FOR */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                            A practical fit between spreadsheets and enterprise WMS projects.
                        </h2>
                        <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
                            BayWhiz is designed for warehouse and distribution teams that need more control than spreadsheets, emails, and whiteboards provide—without taking on a six-month software overhaul.
                        </p>
                        <p className="text-sm sm:text-base text-slate-500">
                            Built for warehouse, distribution, food &amp; beverage, cold storage, manufacturing, and 3PL receiving operations.
                        </p>
                    </div>
                    <div className="bg-slate-50 p-7 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
                        <h3 className="font-bold text-slate-900 mb-4 text-lg">Designed for operations with:</h3>
                        <ul className="space-y-3">
                            {[
                                'Multiple receiving or shipping dock doors',
                                'Daily inbound or outbound carrier appointments',
                                'Purchase orders that must be validated before booking',
                                'Supervisors coordinating dock changes across shifts',
                                'A need for timestamped check-in, dwell, and cancellation records',
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3">
                                    <CheckCircle2 size={18} className="text-teal-600 mt-1 shrink-0" />
                                    <span className="text-slate-700 text-sm sm:text-base">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* 3. SOLUTIONS */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto space-y-20">
                    {/* Solution 1 */}
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center border border-teal-200 mb-6">
                                <CalendarDays size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900">
                                Move the dock schedule out of spreadsheets.
                            </h2>
                            <div className="mb-4 p-5 bg-white border border-slate-200 rounded-xl shadow-sm border-l-4 border-l-slate-400">
                                <h3 className="font-bold text-slate-900 text-xs mb-1 uppercase tracking-wider">The Problem</h3>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    Appointments are split across spreadsheets, whiteboards, shared inboxes, and phone calls.
                                </p>
                            </div>
                            <div className="mb-6 p-5 bg-white border border-slate-200 rounded-xl shadow-sm border-l-4 border-l-teal-600">
                                <h3 className="font-bold text-slate-900 text-xs mb-1 uppercase tracking-wider">BayWhiz Outcome</h3>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    Put scheduled appointments, dock door assignments, pending carrier requests, and live arrival status into one shared board.
                                </p>
                            </div>
                            <Link
                                to="/product"
                                className="inline-flex items-center gap-2 text-teal-700 font-semibold text-sm hover:text-teal-800 transition-colors"
                            >
                                See the Product <ArrowRight size={16} />
                            </Link>
                        </div>
                        <div className="rounded-2xl shadow-xl border border-slate-200 overflow-hidden bg-white">
                            <img
                                src="/landing/real-warehouse-schedule.png"
                                alt="BayWhiz live warehouse schedule interface"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    {/* Solution 2 */}
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1 rounded-2xl shadow-xl border border-slate-200 overflow-hidden bg-white">
                            <img
                                src="/landing/real-carrier-booking.png"
                                alt="BayWhiz carrier self-service booking screen"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center border border-teal-200 mb-6">
                                <LinkIcon size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900">
                                Give carriers one place to request appointment time.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
                                Reduce scheduling coordination through phone and email while your warehouse retains complete control over approvals and dock doors.
                            </p>
                            <ul className="space-y-3 text-sm text-slate-700">
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={17} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>Warehouse shares its public facility booking link</span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={17} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>Carrier validates PO number and selects an available slot</span>
                                </li>
                                <li className="flex items-start gap-2.5">
                                    <CheckCircle2 size={17} className="text-teal-600 shrink-0 mt-0.5" />
                                    <span>Warehouse approves, proposes a revised time, or declines</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Solution 3 */}
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center border border-teal-200 mb-6">
                                <LayoutDashboard size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900">
                                Track on-site check-in, dock dispatch, and preserved audit history.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-4">
                                When several dock doors are active, knowing which truck is checked in, which door is unloading, and who approved or reassigned a load prevents costly dock confusion.
                            </p>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                                Even when a carrier cancels an approved booking, BayWhiz releases the door automatically while preserving the original approval and dock assignment record.
                            </p>
                        </div>
                        <div className="rounded-2xl shadow-xl border border-slate-200 overflow-hidden bg-white">
                            <img
                                src="/landing/real-checkin-checkout.png"
                                alt="BayWhiz checked-in appointment execution and receiving reconciliation"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    {/* Solution 4 */}
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="order-2 lg:order-1 rounded-2xl shadow-xl border border-slate-200 overflow-hidden bg-white">
                            <img
                                src="/landing/real-dwell-reporting.png"
                                alt="BayWhiz Dwell & Detention Analysis dashboard"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center border border-teal-200 mb-6">
                                <BarChart3 size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900">
                                Measure dwell time and dock performance from actual timestamps.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
                                Move beyond anecdotal shift feedback. Track how long trucks spend between check-in and completion, compare dwell against your facility grace period, and export printable PDF dwell reports or schedule CSV records whenever needed.
                            </p>
                            <div className="grid grid-cols-2 gap-3 text-sm font-semibold text-slate-800">
                                <div className="p-3.5 bg-white rounded-xl border border-slate-200 flex items-center gap-2">
                                    <Clock size={16} className="text-teal-600 shrink-0" />
                                    Avg Dwell &amp; Wait Time
                                </div>
                                <div className="p-3.5 bg-white rounded-xl border border-slate-200 flex items-center gap-2">
                                    <CheckCircle2 size={16} className="text-teal-600 shrink-0" />
                                    Detention Rate Tracking
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. FIT */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-4xl mx-auto bg-slate-50 rounded-2xl p-8 sm:p-12 border border-slate-200 shadow-sm">
                    <div className="grid md:grid-cols-[1fr_1.5fr] gap-8 items-center">
                        <div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
                                BayWhiz is built for your facility if&hellip;
                            </h2>
                        </div>
                        <div>
                            <ul className="space-y-4">
                                {[
                                    'Your team coordinates dock appointments in spreadsheets or email',
                                    'Carriers call or email to request and change delivery windows',
                                    'You manage multiple receiving or shipping dock doors',
                                    'You need clear check-in timestamps and dwell visibility',
                                    'You want structured dock scheduling without replacing your WMS',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3.5">
                                        <CheckCircle2 size={20} className="text-teal-600 shrink-0 mt-0.5" />
                                        <span className="text-slate-700 text-base">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. FINAL CTA */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 text-center border-t border-slate-200">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl sm:text-5xl font-bold mb-6 text-slate-900 leading-tight">
                        Ready to take control of your dock schedule?
                    </h2>
                    <p className="text-base sm:text-xl text-slate-600 mb-10 leading-relaxed">
                        Start a 30-day full-access trial and replace manual appointment coordination with one live schedule.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-4 mb-6">
                        <Link
                            to="/request-trial"
                            className="w-full sm:w-auto px-8 py-4 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors shadow-md inline-flex items-center justify-center gap-2"
                        >
                            Request Free Trial
                            <ArrowRight size={18} />
                        </Link>
                        <Link
                            to="/pricing"
                            className="w-full sm:w-auto px-8 py-4 bg-white text-slate-800 border border-slate-300 rounded-xl font-semibold hover:bg-slate-50 transition-colors inline-flex items-center justify-center"
                        >
                            View Pricing
                        </Link>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 font-medium">
                        30-day full-access trial &middot; No credit card required &middot; Plans start at $99 USD/month per facility
                    </p>
                </div>
            </section>

            <MarketingFooter variant="light" />
        </div>
    );
};

export default SolutionsPage;
